/**
 * Orthogonal ("elbow") line path strategy — Stage 1.
 *
 * Auto-routes a Z with two bends at the midpoint, Miro-style: the line leaves
 * one endpoint, turns, runs a middle segment, turns again and arrives at the
 * other endpoint. Every segment is exactly horizontal or vertical.
 *
 * Which way it leaves depends on the dominant axis:
 *  - wider than tall  → HVH (leave horizontally, middle vertical segment)
 *  - taller than wide → VHV (leave vertically,   middle horizontal segment)
 *
 * When an endpoint is DOCKED to an element edge, the dominant axis no longer
 * decides that end: the segment touching the edge must leave PERPENDICULAR to
 * it. `hints` carries the docked sides — a left/right edge forces a horizontal
 * leave, a top/bottom edge a vertical leave. This keeps the elbow visually
 * "attached" to the box instead of grazing its side.
 *
 * The bends sit at the midpoint (or at a shared corner), so the whole path
 * stays INSIDE the endpoint bounding box.
 *
 * The middle segment of a Z can be dragged (`hints.midRatio`, a fraction in
 * [0,1]); the docked-perpendicular "L" forms have no middle segment to move.
 */

import {
  type KonvaLineProps,
  type LinePathStrategy,
  type PathEndpointHints,
  type PathEndpointSide,
} from './line-path-strategy.interface.js';

export const ORTHOGONAL_STRATEGY_ID = 'orthogonal';

/** Leave orientation at an endpoint: horizontal or vertical segment. */
type Orient = 'h' | 'v';

/** A left/right edge is vertical → leave horizontally; top/bottom → vertically. */
function orientOf(side: PathEndpointSide | undefined): Orient | undefined {
  if (side === undefined) return undefined;
  return side === 'left' || side === 'right' ? 'h' : 'v';
}

/** Clamp the middle-segment ratio to [0,1]; `undefined` → 0.5 (midpoint). */
function clampRatio(ratio: number | undefined): number {
  if (ratio === undefined || Number.isNaN(ratio)) return 0.5;
  return ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
}

/** Drop consecutive duplicate vertices so zero-length segments never appear. */
function dedupe(points: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    const x = points[i]!;
    const y = points[i + 1]!;
    const n = out.length;
    if (n >= 2 && out[n - 2] === x && out[n - 1] === y) continue;
    out.push(x, y);
  }
  return out;
}

export class OrthogonalPathStrategy implements LinePathStrategy {
  readonly id: string = ORTHOGONAL_STRATEGY_ID;

  computePath(
    start: { x: number; y: number },
    end: { x: number; y: number },
    _controlPoints?: readonly { x: number; y: number }[],
    hints?: PathEndpointHints,
  ): KonvaLineProps {
    const so = orientOf(hints?.startSide);
    const eo = orientOf(hints?.endSide);
    const t = clampRatio(hints?.midRatio);

    // No docked endpoint → auto-route by the dominant axis.
    if (so === undefined && eo === undefined) {
      return { points: this.#autoRoute(start, end, t) };
    }

    // At least one endpoint is docked: honour its perpendicular leave. A single
    // set hint implies the other end via the opposite orientation (a clean
    // one-bend "L"); two set hints are used as-is.
    const startOrient: Orient = so ?? (eo === 'h' ? 'v' : 'h');
    const endOrient: Orient = eo ?? (so === 'h' ? 'v' : 'h');

    return { points: dedupe(this.#route(start, end, startOrient, endOrient, t)) };
  }

  /**
   * Dominant-axis Z-route used when neither endpoint is docked. `t` is the
   * middle-segment position in [0,1] (0.5 = midpoint).
   */
  #autoRoute(
    start: { x: number; y: number },
    end: { x: number; y: number },
    t: number,
  ): number[] {
    if (start.x === end.x || start.y === end.y) {
      return [start.x, start.y, end.x, end.y];
    }
    if (Math.abs(end.x - start.x) >= Math.abs(end.y - start.y)) {
      const mx = start.x + t * (end.x - start.x);
      return [start.x, start.y, mx, start.y, mx, end.y, end.x, end.y];
    }
    const my = start.y + t * (end.y - start.y);
    return [start.x, start.y, start.x, my, end.x, my, end.x, end.y];
  }

  /**
   * Route with an explicit leave orientation at each end. `t` positions the
   * middle segment of the Z forms (h/h, v/v) between the endpoints.
   *  - h/h → HVH (both ends leave horizontally, middle vertical segment)
   *  - v/v → VHV
   *  - h/v → L: horizontal first, vertical last (single bend, `t` unused)
   *  - v/h → L: vertical first, horizontal last (single bend, `t` unused)
   */
  #route(
    start: { x: number; y: number },
    end: { x: number; y: number },
    so: Orient,
    eo: Orient,
    t: number,
  ): number[] {
    const { x: sx, y: sy } = start;
    const { x: ex, y: ey } = end;

    if (so === 'h' && eo === 'h') {
      const mx = sx + t * (ex - sx);
      return [sx, sy, mx, sy, mx, ey, ex, ey];
    }
    if (so === 'v' && eo === 'v') {
      const my = sy + t * (ey - sy);
      return [sx, sy, sx, my, ex, my, ex, ey];
    }
    if (so === 'h' /* && eo === 'v' */) {
      return [sx, sy, ex, sy, ex, ey];
    }
    // so === 'v' && eo === 'h'
    return [sx, sy, sx, ey, ex, ey];
  }
}
